<template>
  <div>
    <!-- Table with Action column + dynamic columns -->
    <table>
      <thead>
        <tr>
          <!-- Action column header -->
          <th>Action</th>
          <!-- Dynamic column headers -->
          <th v-for="col in tableColumns" :key="col.key">
            <div @click="setSort(col.key)" class="sort-header">
              {{ col.key }}
              <span v-if="sort.key === col.key">
                {{ sort.order === "asc" ? "↑" : "↓" }}
              </span>
            </div>
            <!-- (existing filter UI unchanged) -->
            <div class="sort-icon">
              <select v-model="filters[col.key].op">
                <option value="equals">Equals</option>
                <option value="contains">Contains</option>
                <option v-if="col.type === 'number' || col.type === 'date'" value="lt">
                  Less than
                </option>
                <option v-if="col.type === 'number' || col.type === 'date'" value="lte">
                  Less than or equal
                </option>
                <option v-if="col.type === 'number' || col.type === 'date'" value="gt">
                  Greater than
                </option>
                <option v-if="col.type === 'number' || col.type === 'date'" value="gte">
                  Greater than or equal
                </option>
              </select>
              <template v-if="filters[col.key].op === 'contains'">
                <input type="text" v-model="filters[col.key].value" :placeholder="'Filter by ' + col.key" />
              </template>
              <template v-else>
                <template v-if="col.type === 'number' || col.type === 'date'">
                  <input :type="col.type" v-model="filters[col.key].value" :placeholder="'Filter by ' + col.key" />
                </template>
                <template v-else-if="
                  col.type === 'string' && filters[col.key].op === 'equals'
                ">
                  <select v-model="filters[col.key].value">
                    <option value="">All</option>
                    <option v-for="opt in getUniqueOptions(col.key)" :key="opt" :value="opt">
                      {{ opt }}
                    </option>
                  </select>
                </template>
                <template v-else>
                  <input type="text" v-model="filters[col.key].value" :placeholder="'Filter by ' + col.key" />
                </template>
              </template>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(row, rowIndex) in processedData" :key="rowIndex">
          <!-- Action button -->
          <td>
            <button @click="emitNavigate(row)">Go</button>
          </td>

          <!-- Data cells -->
          <td v-for="col in tableColumns" :key="col.key">
            <template v-if="col.type === 'boolean'">
              <!-- spinner while loading -->
              <template v-if="loadingFlags[rowIndex] && loadingFlags[rowIndex][col.key]">
                <div class="spinner"></div>
              </template>
              <!-- otherwise the checkbox -->
              <template v-else>
                <input type="checkbox" :checked="row[col.key]" @change="
                  onCheckboxChange(rowIndex, col, $event.target.checked)
                  " />
              </template>
            </template>
            <template v-else>
              {{ row[col.key] }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Export button -->
    <button @click="exportCSV">Export CSV</button>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken, useKeycloak } from "../keycloak-wrapper.ts";

export default {
  name: "DataTable",
  emits: ["navigate"],
  data() {
    return {
      tableData: [],
      sort: { key: "", order: "asc" },
      filters: {},
      loadingFlags: {}, // track loading per [rowIndex][columnKey]
      profile: null,
    };
  },
  computed: {
    // Build a list of visible columns, inferring type (string/number/date/boolean),
    // and dropping any whose key is "index" (case‐insensitive).
    tableColumns() {
      if (!this.tableData.length) return [];
      return Object.keys(this.tableData[0])
        .filter(
          (key) => key.toLowerCase() !== "index" && key.toLowerCase() !== "row"
        )
        .map((key) => {
          const sample = this.tableData.find(
            (r) => r[key] !== null && r[key] !== undefined
          )?.[key];
          let type = "string";
          if (typeof sample === "boolean") type = "boolean";
          else if (typeof sample === "number") type = "number";
          else if (!isNaN(Date.parse(sample))) type = "date";
          return { key, type };
        });
    },

    // Apply filters and sorting exactly as before.
    processedData() {
      let data = [...this.tableData];

      // (filtering logic unchanged...)
      data = data.filter((row) => {
        return this.tableColumns.every((col) => {
          const filter = this.filters[col.key];
          if (filter && filter.value !== "") {
            const val = row[col.key];
            if (filter.op === "contains") {
              return String(val)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            } else {
              // number / date / equals comparisons here...
              // (same as your existing code)
              // ...
            }
          }
          return true;
        });
      });

      // Sorting stage: only if sort.key is one of the visible columns
      if (
        this.sort.key &&
        this.tableColumns.some((c) => c.key === this.sort.key)
      ) {
        const currentCol = this.tableColumns.find(
          (c) => c.key === this.sort.key
        );
        data.sort((a, b) => {
          let aVal = a[this.sort.key];
          let bVal = b[this.sort.key];

          // date comparison
          if (currentCol.type === "date") {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
          }

          if (aVal > bVal) return this.sort.order === "asc" ? 1 : -1;
          if (aVal < bVal) return this.sort.order === "asc" ? -1 : 1;
          return 0;
        });
      }

      return data;
    },
  },

  watch: {
    tableColumns: {
      handler(cols) {
        cols.forEach((col) => {
          if (!this.filters[col.key]) {
            this.filters[col.key] = { op: "equals", value: "" };
          }
        });
      },
      immediate: true,
    },
  },

  methods: {
    async fetchData() {
      try {
        const token = await getToken();
        const url = api_config.backend + "/getAnalysisRequests";
        const resp = await axios.post(url, this.APIparams, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        this.tableData = resp.data;

        this.profile = await useKeycloak();

        console.log("this.tableData", this.tableData);
        this.tableData.forEach((row, index) => {
          console.log("key, value", row, index);
          this.tableData[index]["row"] = index;
        });
        const allKeys = Object.keys(this.tableData[0]);
        const visibleKeys = allKeys.filter(
          (k) => k.toLowerCase() !== "index" && k.toLowerCase() !== "row"
        );
        this.setSort(visibleKeys[0] || allKeys[0]);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    },

    setSort(columnKey) {
      if (this.sort.key === columnKey) {
        this.sort.order = this.sort.order === "asc" ? "desc" : "asc";
      } else {
        this.sort.key = columnKey;
        this.sort.order = "asc";
      }
    },

    getUniqueOptions(columnKey) {
      const s = new Set();
      this.tableData.forEach((r) => {
        const v = r[columnKey];
        if (v !== null && v !== undefined) s.add(v);
      });
      return Array.from(s);
    },

    exportCSV() {
      if (!this.processedData.length) return;
      const headers = this.tableColumns.map((c) => `"${c.key}"`).join(",");
      const rows = this.processedData.map((r) =>
        this.tableColumns
          .map((c) => {
            const cell = r[c.key] != null ? String(r[c.key]) : "";
            return `"${cell.replace(/"/g, '""')}"`;
          })
          .join(",")
      );
      const blob = new Blob([[headers, ...rows].join("\n")], {
        type: "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "export.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    // emit navigation event
    emitNavigate(row) {
      this.$emit("navigate", row.Site, row.SentinexID, row.Datetime);
    },

    // handle boolean checkbox changes
    setLoading(rowIndex, colKey, isLoading) {
      if (!this.loadingFlags[rowIndex]) {
        this.loadingFlags[rowIndex] = {};
      }
      this.loadingFlags[rowIndex][colKey] = isLoading;
    },

    async onCheckboxChange(rowIndex, column, checked) {
      // start loading
      this.setLoading(rowIndex, column.key, true);

      // grab the actual row object (same reference as in tableData)
      const row = this.processedData[rowIndex];
      const payload = {
        date_time: row.Datetime,
        unit_ID: row.SentinexID,
        site: row.Site,
        addressed: checked,
        index: row.Index,
      };

      try {
        const token = await getToken();
        const resp = await axios.post(
          api_config.backend + "/requestAnalysis",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // only update UI if status 200
        if (resp.status === 200) {
          // because processedData wraps tableData items by reference,
          // updating row here reflects in the tableData source
          this.tableData[row.row][column.key] = checked;
          if (column.key === "Addressed") {
            const isAuthenticated = !this.profile?.isAuthenticated.value;
            if (!isAuthenticated) {
              throw Error("profile not authenticated or initialised");
            }
            if (checked) {
              this.tableData[row.row]["AddressedBy"] = this.profile.username;
            } else {
              this.tableData[row.row]["AddressedBy"] = "";
            }
          }
        } else {
          console.warn("Server returned non-200:", resp.status);
        }
      } catch (err) {
        console.error("Failed to update addressed flag:", err);
      } finally {
        // clear loading
        this.setLoading(rowIndex, column.key, false);
      }
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: var(--spacing-ml);
}

.sort-header {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.sort-icon {
  margin-top: var(--spacing-xs);
}

thead th {
  padding: var(--spacing-sm);
  border: 1px solid var(--colour-border-light);
  vertical-align: top;
}

tbody td {
  padding: var(--spacing-sm);
  border: 1px solid var(--colour-border-light);
}

select {
  width: 30%;
}

input {
  width: 70%;
}

input,
select {
  box-sizing: border-box;
  margin-top: var(--spacing-xs);
  padding: var(--spacing-2xs);
}

button {
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  cursor: pointer;
}

/* spinner */
.spinner {
  width: 1em;
  height: 1em;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--colour-text-dark);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

table {
  width: 100%;
  margin: var(--spacing-xs);
  border-collapse: collapse;
  margin-bottom: var(--spacing-md);
}

thead th,
tbody td {
  border: 1px solid var(--colour-border-light);
  margin: 0;
  padding: var(--spacing-2xs);
}

thead th {
  padding: var(--spacing-xs);
  font-weight: 800;
}

thead th.sortable {
  cursor: pointer;
  background: #f4f4f4;
  user-select: none;
}

td button {
  font-size: var(--font-size-md);
  height: var(--control-height-sm);
  margin: 0;
  padding: 0 var(--spacing-md);
  background: var(--color-primary);
  border: 1px solid rgb(203, 213, 225);
  color: var(--colour-bg-light);
  border-radius: var(--radius-md);
  transition: 0.2s ease;
  cursor: pointer;
}

td button:hover {
  background-color: var(--color-primary-hover);
}
</style>
